import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { Button, Color, DateInput, Flex, Icon, Modal, Select, Text } from "src/elements";
import { MultiFileUploader } from "src/components";
import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { useAsyncState } from "src/hooks/useAsyncState";
import { useTranslation } from "src/i18n";
import {
  ResponsePostImportJdaBaseline,
  RoutePostImportJdaBaseline,
} from "@CommonApi/import-jda-baseline";
import { Category } from "@CommonApi/categories";
import { importJdaBaselineModalDataAtom } from "./store/atoms";
import {
  assignFilesToRoles,
  classifyJdaFile,
  JdaFileBuckets,
  JdaFileRole,
  jdaFileKey,
} from "./classifyJdaFile";

const EMPTY_BUCKETS: JdaFileBuckets = {
  site: null,
  shelf: null,
  articleMaster: null,
  unclassified: [],
};

const ROLES: JdaFileRole[] = ["site", "shelf", "articleMaster"];

// DateInput reports the browser date input's valueAsDate, which is always anchored to UTC
// midnight of the selected day — read it back via UTC getters, not local ones, or the date can
// shift a day earlier in any timezone west of UTC.
const formatBatchDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface Props {
  category: Category;
}

export const ModalImportJdaBaseline = () => {
  const [data, setData] = useAtom(importJdaBaselineModalDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  if (!data) {
    return null;
  }

  return <ModalImportJdaBaselineInner category={data} />;
};

const RoleChecklistRow = ({ role, file }: { role: JdaFileRole; file: File | null }) => {
  const { tDynamic } = useTranslation("planogramDelivery");

  return (
    <Flex align="center" gap="6px">
      {file ? (
        <Icon name="checkmarkCircle" size={14} color={Color.primaryActive} />
      ) : (
        <Text variant="small1" color={Color.textSecondary}>
          —
        </Text>
      )}
      <Text variant="small1" color={file ? Color.primaryActive : Color.textSecondary}>
        {tDynamic(`jdaImport.roleLabels.${role}`)}
        {file ? ` — ${file.name}` : ` ${tDynamic("jdaImport.notYetAdded")}`}
      </Text>
    </Flex>
  );
};

const ModalImportJdaBaselineInner = ({ category }: Props) => {
  const { hasLoader } = useLoaders();
  const { postImportJdaBaselineApi } = useApi();
  const { t, tDynamic } = useTranslation("planogramDelivery");

  const roleOptions = ROLES.map((role) => ({
    label: tDynamic(`jdaImport.roleLabels.${role}`),
    value: role,
  }));

  const formatResultSummary = (result: ResponsePostImportJdaBaseline): string =>
    t("jdaImport.resultSummary", {
      imported: result.imported_count,
      skippedEmpty: result.skipped_empty_count,
      skippedExisting: result.skipped_existing_count,
    });

  const [files, setFiles] = useState<File[]>([]);
  const [roleOverrides, setRoleOverrides] = useState<Record<string, JdaFileRole>>({});
  const [buckets, setBuckets] = useState<JdaFileBuckets>(EMPTY_BUCKETS);
  const [batchDate, setBatchDate] = useState<Date | null>(null);
  const [result, setResult] = useAsyncState<ResponsePostImportJdaBaseline | null>(null);

  const isImporting = hasLoader(RoutePostImportJdaBaseline);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const roles = await Promise.all(
        files.map((file) => roleOverrides[jdaFileKey(file)] ?? classifyJdaFile(file)),
      );
      const isManual = files.map((file) => jdaFileKey(file) in roleOverrides);
      if (!cancelled) {
        setBuckets(assignFilesToRoles(files, roles, isManual));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [files, roleOverrides]);

  const assignRoleManually = (file: File, role: JdaFileRole) => {
    setRoleOverrides((prev) => ({ ...prev, [jdaFileKey(file)]: role }));
  };

  const importJdaBaseline = () => {
    if (!buckets.site || !buckets.shelf || !buckets.articleMaster || !batchDate) return;

    setResult(
      postImportJdaBaselineApi({
        jda_site_file: buckets.site,
        jda_shelf_file: buckets.shelf,
        article_master_file: buckets.articleMaster,
        category_id: category.id.toString(),
        batch_date: formatBatchDate(batchDate),
      }),
    );
  };

  return (
    <Modal
      name="ModalImportJdaBaseline"
      title={t("jdaImport.modalTitle", { categoryCode: category.code })}
      width="500px"
    >
      <Flex padding="10px" column gap="16px">
        <MultiFileUploader
          fileType="jda-baseline"
          maxFiles={3}
          isLoading={isImporting}
          onChange={setFiles}
        />

        <Flex column gap="4px">
          <RoleChecklistRow role="site" file={buckets.site} />
          <RoleChecklistRow role="shelf" file={buckets.shelf} />
          <RoleChecklistRow role="articleMaster" file={buckets.articleMaster} />
        </Flex>

        {buckets.unclassified.length > 0 && (
          <Flex column gap="8px">
            <Text variant="small1" color={Color.yellow}>
              {t(
                buckets.unclassified.length === 1
                  ? "jdaImport.couldntIdentifySingular"
                  : "jdaImport.couldntIdentifyPlural",
              )}
            </Text>

            {buckets.unclassified.map((file) => (
              <Flex key={jdaFileKey(file)} align="center" gap="8px">
                <Flex flexGrow={1}>
                  <Text variant="small2">{file.name}</Text>
                </Flex>
                <Select
                  width="180px"
                  placeholder={t("jdaImport.chooseType")}
                  options={roleOptions}
                  value={roleOverrides[jdaFileKey(file)] ?? null}
                  setValue={(role: JdaFileRole) => assignRoleManually(file, role)}
                />
              </Flex>
            ))}
          </Flex>
        )}

        <DateInput
          title={t("jdaImport.batchDate")}
          value={batchDate}
          setValue={setBatchDate}
          isDisabled={isImporting || Boolean(result)}
        />

        {result && (
          <Text variant="small1" color={Color.successText}>
            {formatResultSummary(result)}
          </Text>
        )}

        <Button
          isDisabled={
            !buckets.site ||
            !buckets.shelf ||
            !buckets.articleMaster ||
            !batchDate ||
            isImporting ||
            Boolean(result)
          }
          isLoading={isImporting}
          onClick={importJdaBaseline}
        >
          {t("jdaImport.upload")}
        </Button>
      </Flex>
    </Modal>
  );
};
